// Housekeeping · fase 4 (951): limpieza profunda por rotación, amenities y
// lencería por stock de piso (reposición = salida de la bodega del hotel como
// consumo interno) y rondas de áreas públicas. Lo usa Housekeeping.tsx.
import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { AlertTriangle, Brush, ClipboardList, PackagePlus, Plus } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/Button';
import { getErrorMessage } from '@/lib/api/client';
import { Campo, Panel, fieldCls, fmtFecha, hoyISO, hotelRpc, money, QK, sumarDias, etiquetaPiso, type CamareraHk, useDialogoEnLinea } from './shared';

interface Profunda { id: string; numero: string; piso: string | null; ultima: string | null; dias: number | null; vencida: boolean; libre_hoy: boolean; programada: boolean; camarera: string | null; tarea_estado: string | null }
interface Insumo { id: string; nombre: string; categoria: 'amenity' | 'lenceria' | 'limpieza'; unidad: string; producto_id: string | null; producto: string | null; consumo: Record<string, number>; par_piso: number; activo: boolean; stock_bodega: number | null; costo: number | null }
interface StockPisos { pisos: string[]; filas: { insumo_id: string; nombre: string; unidad: string; categoria: string; par: number; vinculado: boolean; stock: Record<string, number> }[] }
interface Consumo { habitaciones_ocupadas: number; insumos: { nombre: string; unidad: string; categoria: string; consumido: number; repuesto: number; diferencia_conteo: number; costo_repuesto: number; por_habitacion: number | null }[] }
export interface AreaEstado { id: string; nombre: string; frecuencia_horas: number; pasos: string[]; ultima_at: string | null; ultima_por: string | null; vencida: boolean }

const TIPOS_LIMPIEZA: [string, string][] = [['salida', 'Salida'], ['ocupada', 'Ocupada'], ['libre', 'Libre'], ['profunda', 'Profunda']];
const CAT_LBL: Record<string, string> = { amenity: 'Amenity', lenceria: 'Lencería', limpieza: 'Limpieza' };
export const haceCuanto = (iso: string | null) => {
  if (!iso) return 'nunca';
  const min = Math.round((Date.now() - Date.parse(iso)) / 60000);
  return min < 60 ? `hace ${min} min` : min < 1440 ? `hace ${Math.round(min / 60)} h` : `hace ${Math.round(min / 1440)} d`;
};

// ── Limpieza profunda por rotación ─────────────────────────────────────────
export function Profundas({ camareras }: { camareras: CamareraHk[] }) {
  const dlg = useDialogoEnLinea();
  const qc = useQueryClient();
  const { data = [] } = useQuery<Profunda[]>({ queryKey: [QK, 'hk-profundas'], queryFn: () => hotelRpc('hk_profundas', {}) });
  const [sel, setSel] = useState<Record<string, string>>({});
  const [soloVencidas, setSoloVencidas] = useState(true);
  const programar = async (h: Profunda, justificacion?: string): Promise<void> => {
    try {
      await hotelRpc('hk_profunda_programar', { _hab: h.id, _camarera: sel[h.id], _justificacion: justificacion ?? null });
      toast.success(`Profunda de la ${h.numero} programada para hoy`);
      void qc.invalidateQueries({ queryKey: [QK] });
    } catch (e) {
      const msg = getErrorMessage(e);
      if (!msg.startsWith('TOPE:')) { toast.error(msg); return; }
      const r = await dlg.pedir('Supera el tope de créditos', [{ key: 'j', label: 'Justificación (queda registrada)', minimo: 5, multilinea: true }],
        { detalle: msg.replace(/^TOPE:\s*/, ''), aceptar: 'Programar igual' });
      if (r) await programar(h, r.j.trim());
    }
  };
  const vencidas = data.filter((h) => h.vencida).length;
  const lista = data.filter((h) => !soloVencidas || h.vencida || h.programada);
  return (
    <div className="space-y-3 text-sm">
      {dlg.ui}
      <div className="flex flex-wrap items-center justify-between gap-2">
        <p className="max-w-3xl text-xs text-muted-foreground">
          Cada habitación necesita una limpieza profunda (colchón, cortinas, detrás de muebles, rejillas) cada cierto número de días (Ajustes).
          Prográmela en una habitación libre y sin llegada hoy: cuenta 4 créditos y tiene su propio checklist.
        </p>
        <label className="flex items-center gap-1 text-xs"><input type="checkbox" checked={soloVencidas} onChange={(e) => setSoloVencidas(e.target.checked)} /> Solo vencidas ({vencidas})</label>
      </div>
      <div className="overflow-x-auto rounded-md border">
        <table className="w-full text-sm">
          <thead><tr className="border-b text-left text-xs text-muted-foreground">
            <th className="py-2 pl-3 pr-2">N°</th><th className="py-2 pr-2">Habitación</th><th className="py-2 pr-2">Última profunda</th><th className="py-2 pr-2">Hoy</th><th className="py-2 pr-2"></th>
          </tr></thead>
          <tbody>
            {lista.length === 0 && <tr><td colSpan={5} className="py-6 text-center text-muted-foreground">Todas las profundas al día ✓</td></tr>}
            {lista.map((h, i) => (
              <tr key={h.id} className="border-b last:border-0">
                <td className="py-1.5 pl-3 pr-2 text-muted-foreground">{i + 1}</td>
                <td className="py-1.5 pr-2"><span className="font-semibold">{h.numero}</span> <span className="text-xs text-muted-foreground">{etiquetaPiso(h.piso)}</span></td>
                <td className={`py-1.5 pr-2 ${h.vencida ? 'font-medium text-red-700 dark:text-red-400' : ''}`}>{h.ultima ? `${fmtFecha(h.ultima)} · hace ${h.dias} días` : 'Nunca registrada'}</td>
                <td className="py-1.5 pr-2 text-xs">{h.programada ? `Programada · ${h.camarera ?? '—'}${h.tarea_estado ? ` · ${h.tarea_estado.replace('_', ' ')}` : ''}` : h.libre_hoy ? 'Libre' : <span className="text-muted-foreground">Ocupada o con llegada</span>}</td>
                <td className="py-1.5 pr-2 text-right whitespace-nowrap">
                  {!h.programada && h.libre_hoy && (
                    <span className="inline-flex gap-1">
                      <select className="h-7 rounded border bg-background px-1 text-xs" value={sel[h.id] ?? ''} onChange={(e) => setSel({ ...sel, [h.id]: e.target.value })}>
                        <option value="">— Camarera —</option>
                        {camareras.filter((c) => c.activo).map((c) => <option key={c.id} value={c.id}>{c.nombre} ({c.creditos_hoy}/{c.tope})</option>)}
                      </select>
                      <Button size="sm" className="h-7 px-2 text-xs" disabled={!sel[h.id]} onClick={() => void programar(h)}><Brush className="mr-1 h-3 w-3" /> Programar hoy</Button>
                    </span>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
          {lista.length > 0 && <tfoot><tr className="border-t-2 bg-muted/30 font-semibold"><td colSpan={5} className="py-2 pl-3">Total: {lista.length} · vencidas {vencidas} de {data.length}</td></tr></tfoot>}
        </table>
      </div>
    </div>
  );
}

// ── Amenities y lencería por piso ──────────────────────────────────────────
export function Insumos({ puedeEditar }: { puedeEditar: boolean }) {
  const qc = useQueryClient();
  const { data: st } = useQuery<StockPisos>({ queryKey: [QK, 'hk-stock'], queryFn: () => hotelRpc('hk_stock_pisos', {}) });
  const [accion, setAccion] = useState<{ tipo: 'reponer' | 'conteo'; piso: string } | null>(null);
  const [cant, setCant] = useState<Record<string, string>>({});
  const [catalogo, setCatalogo] = useState(false);
  const [desde, setDesde] = useState(sumarDias(hoyISO(), -29));
  const [hasta, setHasta] = useState(hoyISO());
  const { data: cons } = useQuery<Consumo>({ queryKey: [QK, 'hk-consumo', desde, hasta], queryFn: () => hotelRpc('hk_consumo', { _desde: desde, _hasta: hasta }) });
  const guardar = useMutation({
    mutationFn: () => hotelRpc<{ valor_inventario?: number }>(accion?.tipo === 'reponer' ? 'hk_reponer_piso' : 'hk_conteo_piso', {
      _piso: accion?.piso, _items: Object.entries(cant).filter(([, v]) => v !== '').map(([insumo_id, v]) => ({ insumo_id, cantidad: Number(v) })) }),
    onSuccess: (r) => {
      toast.success(accion?.tipo === 'reponer' ? `Piso repuesto${r.valor_inventario ? ` · ${money(r.valor_inventario)} salieron de la bodega` : ''}` : 'Conteo registrado');
      setAccion(null); setCant({}); void qc.invalidateQueries({ queryKey: [QK] });
    },
    onError: (e) => toast.error(getErrorMessage(e)),
  });
  const abrir = (tipo: 'reponer' | 'conteo', piso: string) => {
    const pre: Record<string, string> = {};
    (st?.filas ?? []).forEach((f) => {
      const actual = Number(f.stock[piso] ?? 0);
      if (tipo === 'reponer') { const falta = Math.max(0, Number(f.par) - actual); if (falta > 0) pre[f.insumo_id] = String(Math.ceil(falta)); }
    });
    setCant(pre); setAccion({ tipo, piso });
  };
  const bajo = (f: StockPisos['filas'][number], p: string) => Number(f.par) > 0 && Number(f.stock[p] ?? 0) < Number(f.par) * 0.3;
  return (
    <div className="space-y-4 text-sm">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <p className="max-w-3xl text-xs text-muted-foreground">
          Cada limpieza terminada descuenta del stock de su piso lo que se repone por tipo (catálogo). Al reponer un piso, los insumos vinculados a un producto salen
          de la bodega de housekeeping como consumo interno (con su costo). El conteo físico corrige el stock. En rojo: por debajo del 30 % del par.
        </p>
        {puedeEditar && <Button size="sm" variant="outline" onClick={() => setCatalogo(!catalogo)}><ClipboardList className="mr-1 h-4 w-4" /> Catálogo de insumos</Button>}
      </div>
      {catalogo && <CatalogoInsumos onClose={() => setCatalogo(false)} />}

      {accion && st && (
        <Panel title={`${accion.tipo === 'reponer' ? 'Reponer' : 'Conteo físico'} · ${etiquetaPiso(accion.piso)}`} onClose={() => setAccion(null)}>
          <div className="space-y-2">
            <p className="text-xs text-muted-foreground">{accion.tipo === 'reponer' ? 'Cantidades que se suben al piso (sugeridas para llegar al par).' : 'Lo que hay físicamente en el piso: reemplaza al stock del sistema.'}</p>
            <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-3">
              {st.filas.map((f) => (
                <label key={f.insumo_id} className="flex items-center justify-between gap-2 rounded border px-2 py-1">
                  <span>{f.nombre} <span className="text-xs text-muted-foreground">({Number(f.stock[accion.piso] ?? 0)} / par {f.par}{f.vinculado ? ' · bodega' : ''})</span></span>
                  <input className="h-8 w-20 rounded border bg-background px-1 text-right" inputMode="decimal" value={cant[f.insumo_id] ?? ''} onChange={(e) => setCant({ ...cant, [f.insumo_id]: e.target.value })} />
                </label>
              ))}
            </div>
            <div className="flex justify-end gap-2">
              <Button variant="outline" size="sm" onClick={() => setAccion(null)}>Cancelar</Button>
              <Button size="sm" isLoading={guardar.isPending} disabled={!Object.values(cant).some((v) => v !== '')} onClick={() => guardar.mutate()}>
                {accion.tipo === 'reponer' ? 'Registrar reposición' : 'Guardar conteo'}
              </Button>
            </div>
          </div>
        </Panel>
      )}

      {st && (
        <div className="overflow-x-auto rounded-md border">
          <table className="w-full text-sm">
            <thead><tr className="border-b text-left text-xs text-muted-foreground">
              <th className="py-2 pl-3 pr-2">N°</th><th className="py-2 pr-2">Insumo</th><th className="py-2 pr-2 text-right">Par/piso</th>
              {st.pisos.map((p) => (
                <th key={p} className="py-2 pr-2 text-right">
                  <div>{etiquetaPiso(p)}</div>
                  {puedeEditar && <div className="flex justify-end gap-1 font-normal">
                    <button type="button" className="underline" onClick={() => abrir('reponer', p)}>reponer</button>
                    <button type="button" className="underline" onClick={() => abrir('conteo', p)}>contar</button>
                  </div>}
                </th>
              ))}
            </tr></thead>
            <tbody>
              {st.filas.length === 0 && <tr><td colSpan={3 + st.pisos.length} className="py-6 text-center text-muted-foreground">Sin insumos activos.</td></tr>}
              {st.filas.map((f, i) => (
                <tr key={f.insumo_id} className="border-b last:border-0">
                  <td className="py-1.5 pl-3 pr-2 text-muted-foreground">{i + 1}</td>
                  <td className="py-1.5 pr-2">{f.nombre} <span className="text-xs text-muted-foreground">{CAT_LBL[f.categoria]} · {f.unidad}</span></td>
                  <td className="py-1.5 pr-2 text-right tabular-nums">{f.par}</td>
                  {st.pisos.map((p) => <td key={p} className={`py-1.5 pr-2 text-right tabular-nums ${bajo(f, p) ? 'font-semibold text-red-700 dark:text-red-400' : ''}`}>{Number(f.stock[p] ?? 0)}</td>)}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <section className="space-y-2">
        <div className="flex flex-wrap items-end gap-2">
          <h2 className="mr-auto font-semibold">Consumo del período{cons ? ` · ${cons.habitaciones_ocupadas} habitaciones ocupadas limpiadas` : ''}</h2>
          <label className="text-xs">Desde<input type="date" className={`${fieldCls} w-40`} value={desde} onChange={(e) => setDesde(e.target.value)} /></label>
          <label className="text-xs">Hasta<input type="date" className={`${fieldCls} w-40`} value={hasta} onChange={(e) => setHasta(e.target.value)} /></label>
        </div>
        <div className="overflow-x-auto rounded-md border">
          <table className="w-full text-sm">
            <thead><tr className="border-b text-left text-xs text-muted-foreground">
              <th className="py-2 pl-3 pr-2">N°</th><th className="py-2 pr-2">Insumo</th><th className="py-2 pr-2 text-right">Consumido</th><th className="py-2 pr-2 text-right">Por hab. ocupada</th>
              <th className="py-2 pr-2 text-right">Repuesto</th><th className="py-2 pr-2 text-right">Diferencia de conteo</th><th className="py-2 pr-2 text-right">Costo repuesto</th>
            </tr></thead>
            <tbody>
              {(cons?.insumos ?? []).map((x, i) => (
                <tr key={x.nombre} className="border-b last:border-0">
                  <td className="py-1.5 pl-3 pr-2 text-muted-foreground">{i + 1}</td><td className="py-1.5 pr-2">{x.nombre} <span className="text-xs text-muted-foreground">{x.unidad}</span></td>
                  <td className="py-1.5 pr-2 text-right tabular-nums">{x.consumido}</td><td className="py-1.5 pr-2 text-right tabular-nums">{x.por_habitacion ?? '—'}</td>
                  <td className="py-1.5 pr-2 text-right tabular-nums">{x.repuesto}</td>
                  <td className={`py-1.5 pr-2 text-right tabular-nums ${Number(x.diferencia_conteo) < 0 ? 'text-red-700 dark:text-red-400' : ''}`}>{x.diferencia_conteo}</td>
                  <td className="py-1.5 pr-2 text-right tabular-nums">{money(x.costo_repuesto)}</td>
                </tr>
              ))}
            </tbody>
            {!!cons?.insumos.length && <tfoot><tr className="border-t-2 bg-muted/30 font-semibold"><td colSpan={6} className="py-2 pl-3">Total</td>
              <td className="py-2 pr-2 text-right tabular-nums">{money(cons.insumos.reduce((a, x) => a + Number(x.costo_repuesto), 0))}</td></tr></tfoot>}
          </table>
        </div>
        <p className="text-[11px] text-muted-foreground">Una diferencia de conteo negativa es faltante frente al consumo estándar: revise el estándar del catálogo o el control del piso.</p>
      </section>
    </div>
  );
}

function CatalogoInsumos({ onClose }: { onClose: () => void }) {
  const qc = useQueryClient();
  const { data = [] } = useQuery<Insumo[]>({ queryKey: [QK, 'hk-insumos'], queryFn: () => hotelRpc('hk_insumos_listar', {}) });
  const [edit, setEdit] = useState<Partial<Insumo> | null>(null);
  const [busca, setBusca] = useState('');
  const { data: prods = [] } = useQuery<{ id: string; codigo: string; nombre: string; unidad: string; stock: number | null; costo: number | null }[]>({
    queryKey: [QK, 'hk-prod', busca], queryFn: () => hotelRpc('hk_productos_buscar', { _q: busca }), enabled: !!edit && busca.length >= 2,
  });
  const guardar = useMutation({
    mutationFn: () => hotelRpc('hk_insumo_guardar', { _d: edit }),
    onSuccess: () => { toast.success('Insumo guardado'); setEdit(null); void qc.invalidateQueries({ queryKey: [QK] }); },
    onError: (e) => toast.error(getErrorMessage(e)),
  });
  return (
    <Panel title="Catálogo de insumos" onClose={onClose}>
      <div className="space-y-3 text-sm">
        <div className="overflow-x-auto rounded-md border">
          <table className="w-full text-sm">
            <thead><tr className="border-b text-left text-xs text-muted-foreground">
              <th className="py-2 pl-3 pr-2">N°</th><th className="py-2 pr-2">Insumo</th><th className="py-2 pr-2">Producto de inventario</th>
              {TIPOS_LIMPIEZA.map(([, l]) => <th key={l} className="py-2 pr-2 text-right">{l}</th>)}<th className="py-2 pr-2 text-right">Par</th><th className="py-2 pr-2" />
            </tr></thead>
            <tbody>
              {data.map((s, i) => (
                <tr key={s.id} className={`border-b last:border-0 ${s.activo ? '' : 'text-muted-foreground line-through'}`}>
                  <td className="py-1.5 pl-3 pr-2 text-muted-foreground">{i + 1}</td><td className="py-1.5 pr-2">{s.nombre} <span className="text-xs text-muted-foreground">{CAT_LBL[s.categoria]}</span></td>
                  <td className="py-1.5 pr-2 text-xs">{s.producto ?? <span className="text-muted-foreground">sin vincular</span>}{s.producto && s.stock_bodega != null ? ` · bodega ${s.stock_bodega}` : ''}</td>
                  {TIPOS_LIMPIEZA.map(([k]) => <td key={k} className="py-1.5 pr-2 text-right tabular-nums">{s.consumo?.[k] ?? 0}</td>)}
                  <td className="py-1.5 pr-2 text-right tabular-nums">{s.par_piso}</td>
                  <td className="py-1.5 pr-2 text-right"><Button size="sm" variant="outline" className="h-7 px-2 text-xs" onClick={() => setEdit({ ...s })}>Editar</Button></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {!edit && <Button size="sm" onClick={() => setEdit({ categoria: 'amenity', unidad: 'unidad', consumo: {}, par_piso: 0, activo: true })}><Plus className="mr-1 h-4 w-4" /> Nuevo insumo</Button>}
        {edit && (
          <div className="grid gap-2 rounded-md border p-3 sm:grid-cols-4">
            <Campo label="Nombre *"><input className={fieldCls} value={edit.nombre ?? ''} onChange={(e) => setEdit({ ...edit, nombre: e.target.value })} /></Campo>
            <Campo label="Categoría">
              <select className={fieldCls} value={edit.categoria} onChange={(e) => setEdit({ ...edit, categoria: e.target.value as Insumo['categoria'] })}>
                <option value="amenity">Amenity</option><option value="lenceria">Lencería</option><option value="limpieza">Limpieza</option>
              </select>
            </Campo>
            <Campo label="Unidad"><input className={fieldCls} value={edit.unidad ?? ''} onChange={(e) => setEdit({ ...edit, unidad: e.target.value })} /></Campo>
            <Campo label="Par por piso"><input type="number" step="any" className={fieldCls} value={edit.par_piso ?? 0} onChange={(e) => setEdit({ ...edit, par_piso: Number(e.target.value) })} /></Campo>
            {TIPOS_LIMPIEZA.map(([k, l]) => (
              <Campo key={k} label={`Por limpieza · ${l}`}>
                <input type="number" step="any" min={0} className={fieldCls} value={edit.consumo?.[k] ?? 0}
                  onChange={(e) => setEdit({ ...edit, consumo: { ...(edit.consumo ?? {}), [k]: Number(e.target.value) } })} />
              </Campo>
            ))}
            <div className="sm:col-span-4">
              <Campo label={`Producto de inventario${edit.producto ? `: ${edit.producto}` : ''} (opcional: para descargar de la bodega)`}>
                <input className={fieldCls} placeholder="Buscar producto por nombre o código…" value={busca} onChange={(e) => setBusca(e.target.value)} />
              </Campo>
              {busca.length >= 2 && (
                <div className="mt-1 max-h-40 overflow-y-auto rounded border">
                  {prods.map((p) => (
                    <button key={p.id} type="button" className={`block w-full px-2 py-1 text-left text-xs hover:bg-muted ${edit.producto_id === p.id ? 'bg-primary/10' : ''}`}
                      onClick={() => { setEdit({ ...edit, producto_id: p.id, producto: `${p.codigo} · ${p.nombre}` }); setBusca(''); }}>
                      {p.codigo} · {p.nombre} <span className="text-muted-foreground">{p.unidad}{p.stock != null ? ` · bodega ${p.stock}` : ''}</span>
                    </button>
                  ))}
                  {prods.length === 0 && <div className="px-2 py-1 text-xs text-muted-foreground">Sin resultados</div>}
                </div>
              )}
              {edit.producto_id && <button type="button" className="mt-1 text-xs underline" onClick={() => setEdit({ ...edit, producto_id: null, producto: null })}>Quitar vínculo</button>}
            </div>
            <label className="flex items-center gap-2"><input type="checkbox" checked={edit.activo ?? true} onChange={(e) => setEdit({ ...edit, activo: e.target.checked })} /> Activo</label>
            <div className="flex justify-end gap-2 sm:col-span-3">
              <Button variant="outline" size="sm" onClick={() => setEdit(null)}>Cancelar</Button>
              <Button size="sm" isLoading={guardar.isPending} disabled={!edit.nombre?.trim()} onClick={() => guardar.mutate()}><PackagePlus className="mr-1 h-4 w-4" /> Guardar</Button>
            </div>
          </div>
        )}
      </div>
    </Panel>
  );
}

// ── Áreas públicas: estado en el tablero ───────────────────────────────────
export function AreasEstadoBanda() {
  const { data = [] } = useQuery<AreaEstado[]>({ queryKey: [QK, 'hk-areas'], queryFn: () => hotelRpc('hk_areas_estado', {}), refetchInterval: 60000 });
  if (!data.length) return null;
  return (
    <div className="flex flex-wrap items-center gap-2 text-xs">
      <span className="font-medium text-muted-foreground">Áreas públicas:</span>
      {data.map((a) => (
        <span key={a.id} title={`Cada ${a.frecuencia_horas} h${a.ultima_por ? ` · última por ${a.ultima_por}` : ''}`}
          className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 ${a.vencida ? 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300' : 'bg-emerald-100 text-emerald-800 dark:bg-emerald-900/30 dark:text-emerald-300'}`}>
          {a.vencida && <AlertTriangle className="h-3 w-3" />}{a.nombre} · {haceCuanto(a.ultima_at)}
        </span>
      ))}
    </div>
  );
}

// ── Ajustes de la fase 4: profunda, bodega y áreas ─────────────────────────
export function AjustesF4({ puedeEditar }: { puedeEditar: boolean }) {
  const qc = useQueryClient();
  const { data: aj } = useQuery<{ profunda_dias: number; min_profunda: number; bodega_id: string | null }>({ queryKey: [QK, 'hk-ajustes'], queryFn: () => hotelRpc('hk_ajustes', {}) });
  const { data: bodegas = [] } = useQuery<{ id: string; nombre: string; codigo: string }[]>({ queryKey: [QK, 'hk-bodegas'], queryFn: () => hotelRpc('hk_bodegas', {}) });
  const { data: areas = [] } = useQuery<AreaEstado[]>({ queryKey: [QK, 'hk-areas'], queryFn: () => hotelRpc('hk_areas_estado', {}) });
  const [par, setPar] = useState<Record<string, unknown> | null>(null);
  const [area, setArea] = useState<{ id?: string; nombre: string; frecuencia_horas: number; pasos: string } | null>(null);
  const guardar = useMutation({
    mutationFn: (d: Record<string, unknown>) => hotelRpc('hk_ajustes_guardar', { _d: d }),
    onSuccess: () => { toast.success('Guardado'); setPar(null); void qc.invalidateQueries({ queryKey: [QK] }); }, onError: (e) => toast.error(getErrorMessage(e)),
  });
  const guardarArea = useMutation({
    mutationFn: () => hotelRpc('hk_area_guardar', { _d: { id: area?.id ?? null, nombre: area?.nombre, frecuencia_horas: area?.frecuencia_horas,
      pasos: (area?.pasos ?? '').split('\n').map((x) => x.trim()).filter(Boolean) } }),
    onSuccess: () => { toast.success('Área guardada'); setArea(null); void qc.invalidateQueries({ queryKey: [QK] }); }, onError: (e) => toast.error(getErrorMessage(e)),
  });
  const v = useMemo(() => ({ ...(aj ?? {}), ...(par ?? {}) }) as { profunda_dias?: number; min_profunda?: number; bodega_id?: string | null }, [aj, par]);
  if (!aj) return null;
  return (
    <div className="space-y-4 text-sm">
      <section className="space-y-2">
        <h2 className="font-semibold">Limpieza profunda e insumos</h2>
        <div className="grid gap-3 sm:grid-cols-3">
          <Campo label="Profunda cada (días)"><input type="number" className={fieldCls} disabled={!puedeEditar} value={v.profunda_dias ?? ''} onChange={(e) => setPar({ ...par, profunda_dias: Number(e.target.value) })} /></Campo>
          <Campo label="Minutos estándar: profunda"><input type="number" className={fieldCls} disabled={!puedeEditar} value={v.min_profunda ?? ''} onChange={(e) => setPar({ ...par, min_profunda: Number(e.target.value) })} /></Campo>
          <Campo label="Bodega de housekeeping (de donde salen los insumos)">
            <select className={fieldCls} disabled={!puedeEditar} value={v.bodega_id ?? ''} onChange={(e) => setPar({ ...par, bodega_id: e.target.value || null })}>
              <option value="">— Sin bodega (no descarga inventario) —</option>
              {bodegas.map((b) => <option key={b.id} value={b.id}>{b.codigo} · {b.nombre}</option>)}
            </select>
          </Campo>
        </div>
        {puedeEditar && <Button size="sm" disabled={!par} isLoading={guardar.isPending} onClick={() => guardar.mutate({ parametros: par })}>Guardar</Button>}
      </section>
      <section className="space-y-2">
        <h2 className="font-semibold">Áreas públicas (rondas)</h2>
        <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-4">
          {areas.map((a) => (
            <div key={a.id} className="rounded-md border p-2">
              <div className="flex items-center justify-between"><span className="font-medium">{a.nombre}</span>
                {puedeEditar && <button type="button" className="text-xs underline" onClick={() => setArea({ id: a.id, nombre: a.nombre, frecuencia_horas: a.frecuencia_horas, pasos: (a.pasos ?? []).join('\n') })}>Editar</button>}</div>
              <div className="text-xs text-muted-foreground">Cada {a.frecuencia_horas} h · {(a.pasos ?? []).join(', ')}</div>
            </div>
          ))}
        </div>
        {puedeEditar && !area && <Button size="sm" variant="outline" onClick={() => setArea({ nombre: '', frecuencia_horas: 4, pasos: '' })}><Plus className="mr-1 h-4 w-4" /> Nueva área</Button>}
        {area && (
          <div className="grid gap-2 rounded-md border p-3 sm:grid-cols-3">
            <Campo label="Nombre *"><input className={fieldCls} value={area.nombre} onChange={(e) => setArea({ ...area, nombre: e.target.value })} /></Campo>
            <Campo label="Cada cuántas horas"><input type="number" min={1} max={168} className={fieldCls} value={area.frecuencia_horas} onChange={(e) => setArea({ ...area, frecuencia_horas: Number(e.target.value) })} /></Campo>
            <Campo label="Pasos (uno por línea)"><textarea className={`${fieldCls} h-20`} value={area.pasos} onChange={(e) => setArea({ ...area, pasos: e.target.value })} /></Campo>
            <div className="flex justify-end gap-2 sm:col-span-3">
              <Button variant="outline" size="sm" onClick={() => setArea(null)}>Cancelar</Button>
              <Button size="sm" disabled={!area.nombre.trim()} isLoading={guardarArea.isPending} onClick={() => guardarArea.mutate()}>Guardar área</Button>
            </div>
          </div>
        )}
      </section>
    </div>
  );
}
